import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Badge, Button, Center, Group, Loader, Modal, SimpleGrid, Stack, Switch, Table, Text, TextInput, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, offersApi, type CvLine, type OfferParty, type PartyProfile } from '../../api';
import { PARTY_FIELDS, PARTY_PRIVATE } from '../../format';

type PartyInput = Omit<OfferParty, 'id'>;

const EMPTY: PartyInput = {
  name: '', profession: '', street: '', city: '', phone: '', email: '', chamber: '', chamberNo: '', buildingAuthority: '',
  taxNo: '', accountHolder: '', iban: '', birthDate: '', isCompany: false, contact: '', note: '',
  profile: { career: [], volunteer: [], education: [], qualifications: [], languages: '', software: '' },
};

/**
 * The people and firms who take part in offers - bidder, members of a joint
 * bid, subcontractors - kept once for all offers (template 01_Parameter
 * C38-H50 and the subcontractor register).
 */
export function PartiesPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['offer-parties'], queryFn: offersApi.parties });
  const [editing, setEditing] = useState<OfferParty | 'new' | null>(null);
  const remove = useMutation({ mutationFn: (id: string) => offersApi.removeParty(id), onSettled: () => queryClient.invalidateQueries({ queryKey: ['offer-parties'] }) });
  if (list.isPending) return <Center h={120}><Loader /></Center>;
  if (list.isError) return <Alert color="red">{list.error instanceof ApiError ? list.error.message : 'Die Beteiligten konnten nicht geladen werden.'}</Alert>;
  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Personen und Firmen, die an Angeboten beteiligt sind: Bieter, Mitglieder einer Bietergemeinschaft, Nachunternehmer. Einmal hier gepflegt, im Angebot gewählt. Steuernummer, Kontoinhaber, IBAN und Geburtsdatum gehen in kein Dokument an den Auftraggeber.
      </Text>
      <Group><Button size="xs" onClick={() => setEditing('new')}>Person oder Firma anlegen</Button></Group>
      {list.data.length === 0 ? <Text size="sm" c="dimmed">Noch niemand angelegt.</Text> : (
        <Table.ScrollContainer minWidth={700}>
          <Table verticalSpacing={4} highlightOnHover>
            <Table.Thead><Table.Tr><Table.Th w={30} /><Table.Th>{PARTY_FIELDS.name}</Table.Th><Table.Th>{PARTY_FIELDS.profession}</Table.Th><Table.Th>Anschrift</Table.Th><Table.Th>Kontakt</Table.Th><Table.Th w={80} /></Table.Tr></Table.Thead>
            <Table.Tbody>
              {list.data.map((p) => (
                <Table.Tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setEditing(p)}>
                  <Table.Td>
                    <ActionIcon size="xs" variant="subtle" color="gray" aria-label={`${p.name} löschen`} title="Löschen"
                      onClick={(e) => { e.stopPropagation(); if (window.confirm(`${p.name} löschen? Angebote behalten ihre Kalkulation, verlieren aber den Verweis.`)) remove.mutate(p.id); }}>✕</ActionIcon>
                  </Table.Td>
                  <Table.Td><Text size="sm" fw={500}>{p.name}</Text></Table.Td>
                  <Table.Td><Text size="sm">{p.profession}</Text></Table.Td>
                  <Table.Td><Text size="xs">{[p.street, p.city].filter(Boolean).join(', ')}</Text></Table.Td>
                  <Table.Td><Text size="xs">{[p.phone, p.email].filter(Boolean).join(' · ')}</Text></Table.Td>
                  <Table.Td>{p.isCompany && <Badge size="xs" variant="light">Firma</Badge>}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {editing && <PartyModal party={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </Stack>
  );
}

function PartyModal({ party, onClose }: { party: OfferParty | null; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PartyInput>(() => (party ? (({ id: _, ...rest }) => rest)(party) : EMPTY));
  const save = useMutation({
    mutationFn: () => (party ? offersApi.saveParty(party.id, form) : offersApi.addParty(form).then(() => undefined)),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['offer-parties'] }); onClose(); },
  });
  const field = (key: keyof typeof PARTY_FIELDS): JSX.Element => (
    <TextInput key={key} size="xs" label={PARTY_FIELDS[key]} value={form[key]} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, [key]: v })); }}
      description={(PARTY_PRIVATE as readonly string[]).includes(key) ? 'nur intern, nie im Angebot' : undefined} />
  );
  return (
    <Modal opened onClose={onClose} title={party ? party.name : 'Person oder Firma anlegen'} size="lg">
      <Stack gap="xs">
        <Switch label="Firma (z. B. Nachunternehmer als Unternehmen)" checked={form.isCompany} onChange={(e) => { const v = e.currentTarget.checked; setForm((f) => ({ ...f, isCompany: v })); }} />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
          {(['name', 'profession', 'street', 'city', 'phone', 'email', 'chamber', 'chamberNo', 'buildingAuthority', 'contact'] as const).map(field)}
        </SimpleGrid>
        <Text size="sm" fw={500} mt="xs">Nur intern</Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
          {(['taxNo', 'accountHolder', 'iban', 'birthDate'] as const).map(field)}
        </SimpleGrid>
        <Textarea size="xs" label={PARTY_FIELDS.note} autosize minRows={2} value={form.note} onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, note: v })); }} />
        {!form.isCompany && <ProfileEditor profile={form.profile} onChange={(profile) => setForm((f) => ({ ...f, profile }))} />}
        {save.isError && <Alert color="red" py={6}>{save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!form.name.trim()}>Speichern</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** The CV of a person (AP-102): what the Lebenslauf of an offer is made from. */
function ProfileEditor({ profile, onChange }: { profile: PartyProfile; onChange: (p: PartyProfile) => void }): JSX.Element {
  const lines = (key: 'career' | 'volunteer' | 'education', label: string, hint: string): JSX.Element => (
    <Stack gap={4}>
      <Text size="sm" fw={500}>{label}</Text>
      {profile[key].map((l, i) => (
        <Group key={i} gap={6} wrap="nowrap" align="flex-start">
          <ActionIcon variant="subtle" color="gray" mt={4} aria-label="Zeile entfernen" title="Zeile entfernen" onClick={() => onChange({ ...profile, [key]: profile[key].filter((_, j) => j !== i) })}>✕</ActionIcon>
          <TextInput size="xs" w={130} placeholder="seit 2016" value={l.period} onChange={(e) => { const v = e.currentTarget.value; onChange({ ...profile, [key]: profile[key].map((x, j): CvLine => (j === i ? { ...x, period: v } : x)) }); }} />
          <Textarea size="xs" style={{ flex: 1 }} autosize minRows={1} placeholder={hint} value={l.text} onChange={(e) => { const v = e.currentTarget.value; onChange({ ...profile, [key]: profile[key].map((x, j): CvLine => (j === i ? { ...x, text: v } : x)) }); }} />
        </Group>
      ))}
      <Group><Button size="compact-xs" variant="subtle" onClick={() => onChange({ ...profile, [key]: [...profile[key], { period: '', text: '' }] })}>+ Zeile</Button></Group>
    </Stack>
  );
  return (
    <Stack gap="xs" mt="xs">
      <Text size="sm" fw={600}>Lebenslauf</Text>
      <Text size="xs" c="dimmed">Für die Anlage „Lebenslauf“ eines Angebots. Das Geburtsdatum steht nur darin, wenn es im Angebot ausdrücklich gewählt ist.</Text>
      {lines('career', 'Beruflicher Werdegang', 'Büro, Ort – Tätigkeit, Schwerpunkte')}
      {lines('volunteer', 'Ehrenamt und Mitgliedschaften', 'Verein, Ort – Aufgabe')}
      {lines('education', 'Ausbildung', 'Hochschule – Abschluss')}
      <Textarea size="xs" label="Qualifikation (je Zeile ein Punkt)" autosize minRows={2} value={profile.qualifications.join('\n')} onChange={(e) => onChange({ ...profile, qualifications: e.currentTarget.value.split('\n') })} />
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
        <TextInput size="xs" label="Sprachen" value={profile.languages} onChange={(e) => onChange({ ...profile, languages: e.currentTarget.value })} placeholder="Deutsch (Muttersprache) · Englisch (C1)" />
        <TextInput size="xs" label="Software" value={profile.software} onChange={(e) => onChange({ ...profile, software: e.currentTarget.value })} placeholder="Vectorworks, ArchiCAD …" />
      </SimpleGrid>
    </Stack>
  );
}
